import React, { FC, ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '@wix/yoshi-flow-editor';
import {
  MobileDrawer,
  Text,
  TextTypography as TYPOGRAPHY,
} from 'wix-ui-tpa/cssVars';
import { Dismiss } from '@wix/wix-ui-icons-common';
import { classes } from './MobileSheet.st.css';

export type MobileSheetProps = {
  layer: HTMLElement | null;
  label: string;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
  'data-hook'?: string;
};

export const MobileSheet: FC<MobileSheetProps> = ({
  layer,
  label,
  onClose,
  footer,
  children,
  'data-hook': dataHook,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  if (!layer) {
    return null;
  }

  return createPortal(
    <MobileDrawer
      isOpen
      onRequestClose={onClose}
      className={classes.drawer}
      aria-label={label}
    >
      <div className={classes.sheet} data-hook={dataHook}>
        <div className={classes.header}>
          <Text
            className={classes.title}
            typography={TYPOGRAPHY.smallTitle}
            tagName="h2"
          >
            {label}
          </Text>

          <button
            type="button"
            className={classes.close}
            aria-label={t('widget.general.close.button.label')}
            onClick={onClose}
          >
            <Dismiss />
          </button>
        </div>

        <div className={classes.body}>{children}</div>

        {footer ? <div className={classes.footer}>{footer}</div> : null}
      </div>
    </MobileDrawer>,
    layer,
  );
};
